import {
	autoUpdate,
	FloatingFocusManager,
	FloatingPortal,
	flip,
	offset,
	shift,
	size,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useListNavigation,
	useRole,
} from "@floating-ui/react";
import { useMemo, useRef, useState } from "react";
import { FaChevronDown, FaTimes } from "react-icons/fa";

const SelectInput = ({
	id,
	placeholder,
	value,
	onChange,
	options,
	label,
	displayField = "name",
	renderOption,
	disabled,
	classNameInput = "bg-blue-gray bg-opacity-20",
	classNameSearchInput = "bg-dark-blue",
	classNameSelectInput = "hover:bg-blue-gray hover:bg-opacity-40",
	dataTestId,
}) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [activeIndex, setActiveIndex] = useState(null);
	const baseTestId = dataTestId || id;

	const listRef = useRef([]);

	const { refs, floatingStyles, context } = useFloating({
		open: isDropdownOpen,
		onOpenChange: (open) => {
			setIsDropdownOpen(open);
			if (!open) setSearchTerm("");
		},
		placement: "bottom-start",
		middleware: [
			offset(4),
			flip({ padding: 8 }),
			shift({ padding: 8 }),
			size({
				apply({ rects, elements, availableHeight }) {
					Object.assign(elements.floating.style, {
						width: `${rects.reference.width}px`,
						maxHeight: `${Math.min(240, availableHeight)}px`,
					});
				},
				padding: 8,
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context, { enabled: !disabled });
	const dismiss = useDismiss(context);
	const role = useRole(context, { role: "listbox" });
	const listNav = useListNavigation(context, {
		listRef,
		activeIndex,
		onNavigate: setActiveIndex,
		virtual: true,
		loop: true,
	});

	const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
		click,
		dismiss,
		role,
		listNav,
	]);

	const filteredOptions = useMemo(() => {
		if (!options?.length) return [];
		return options.filter((option) => {
			const textValue = typeof option?.[displayField] === "string" ? option[displayField] : "";
			return textValue.toLowerCase().includes(searchTerm.toLowerCase());
		});
	}, [options, displayField, searchTerm]);

	const handleSelectChange = (selectedValue, selectedText) => {
		onChange(id, selectedValue, selectedText);
		setIsDropdownOpen(false);
		setSearchTerm("");
	};

	const handleClear = (e) => {
		e.stopPropagation();
		onChange(id, null, null);
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && activeIndex != null && filteredOptions[activeIndex]) {
			e.preventDefault();
			const option = filteredOptions[activeIndex];
			handleSelectChange(option.id, option[displayField]);
		}
	};

	return (
		<div
			data-testid={`${baseTestId}-container`}
			className={`relative flex flex-col ${disabled ? "disabled-select" : ""}`}
		>
			<label
				htmlFor={id}
				className="mb-2 flex items-center text-left font-normal text-gray-400 text-xs"
			>
				{label}
			</label>
			<div
				ref={refs.setReference}
				data-testid={`${baseTestId}-trigger`}
				className={`custom-scrollbar flex w-full items-center justify-between rounded-lg border p-2.5 text-xs ${classNameInput}bg-blue-gray cursor-pointer border-gray-600 bg-opacity-20 text-gray-400 placeholder-gray-400 focus:border-gray-500 focus:ring-gray-500`}
				{...getReferenceProps()}
			>
				<div className="flex w-full items-center">
					<span className="flex-1 truncate text-left text-xs" data-testid={`${baseTestId}-value`}>
						{(() => {
							const selected = options?.find((option) => option?.id === value);

							if (!selected) return placeholder;

							return renderOption ? renderOption(selected) : selected?.[displayField];
						})()}
					</span>
					{value !== null && value !== undefined && (
						<FaTimes
							data-testid={`${baseTestId}-clear`}
							className="ml-2 cursor-pointer text-gray-400"
							onClick={(e) => {
								e.stopPropagation();
								handleClear(e);
							}}
						/>
					)}
					<FaChevronDown
						className={`ml-2 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
					/>
				</div>
			</div>

			{isDropdownOpen && (
				<FloatingPortal>
					<FloatingFocusManager context={context} initialFocus={-1}>
						<div
							ref={refs.setFloating}
							style={floatingStyles}
							data-testid={`${baseTestId}-dropdown`}
							className="custom-scrollbar z-50 max-h-60 overflow-y-auto overflow-x-hidden rounded-lg border border-gray-600 bg-navbar-bg"
							{...getFloatingProps({ onKeyDown: handleKeyDown })}
						>
							<input
								data-testid={`${baseTestId}-search`}
								type="text"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder={`Search ${label ? label : ""}`}
								className={`w-full p-2.5 ${classNameSearchInput} border-gray-600 border-b bg-navbar-bg text-gray-400 text-xs placeholder-gray-400 focus:border-blue-gray focus:ring-blue-gray`}
							/>
							{filteredOptions?.length > 0 ? (
								filteredOptions.map((option, index) => (
									<div
										key={option.id}
										ref={(node) => {
											listRef.current[index] = node;
										}}
										data-testid={`${baseTestId}-option-${option.id}`}
										className={`p-2 ${classNameSelectInput} cursor-pointer truncate text-left text-sm text-white hover:bg-blue-gray hover:bg-opacity-40 ${
											activeIndex === index ? "bg-blue-gray bg-opacity-40" : ""
										}`}
										{...getItemProps({
											onClick: () => handleSelectChange(option.id, option[displayField]),
										})}
									>
										{renderOption
											? renderOption(option)
											: typeof option[displayField] === "string"
												? option[displayField].length > 35
													? `${option[displayField].slice(0, 34)}...`
													: option[displayField]
												: option[displayField]}
									</div>
								))
							) : (
								<div className="p-2 text-gray-400">No options found</div>
							)}
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			)}
		</div>
	);
};

export default SelectInput;
